import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import { CLIENT_STATUSES } from "@routebot/constants";
import Button from "shared/components/FormElements/Button";
import AreYouSureModal from "components/shared/AreYouSureModal";
import CheckBox from "shared/components/FormElements/CheckBox";
import { VALIDATOR_NONE } from "shared/util/validators";
import useCustomerPortalApi from "./hooks/useCustomerPortalApi";
import "./CustomerPortal.css";

const schoolRowId = (school) => String(school?.id || school?._id || "");

const InviteCustomerContacts = () => {
  const { t } = useTranslation();
  const { isLoading, error, listContacts, listSchools, inviteContact, removeContact } = useCustomerPortalApi();
  const [contacts, setContacts] = useState([]);
  const [schools, setSchools] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [schoolIds, setSchoolIds] = useState([]);
  const [schoolQuery, setSchoolQuery] = useState("");
  const [schoolBoxKey, setSchoolBoxKey] = useState(0);
  const [localError, setLocalError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [removeId, setRemoveId] = useState("");

  const load = useCallback(async () => {
    const [people, schoolRows] = await Promise.all([listContacts(), listSchools()]);
    setContacts(people);
    setSchools(schoolRows);
  }, [listContacts, listSchools]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const filteredSchools = useMemo(() => {
    const query = schoolQuery.trim().toLowerCase();
    if (!query) return schools;
    return schools.filter((school) => String(school.name || "").toLowerCase().includes(query));
  }, [schools, schoolQuery]);

  const replaceSchoolIds = (nextIds) => {
    setSchoolIds(nextIds);
    setSchoolBoxKey((key) => key + 1);
  };

  const onSchoolToggle = (checkboxId, value) => {
    const schoolId = checkboxId.startsWith("invite_school_") ? checkboxId.slice("invite_school_".length) : checkboxId;
    setSchoolIds((prev) => {
      if (value) {
        if (prev.includes(schoolId)) return prev;
        return [...prev, schoolId];
      }
      return prev.filter((id) => id !== schoolId);
    });
  };

  const onSelectVisible = () => {
    const visibleIds = filteredSchools.map(schoolRowId).filter(Boolean);
    replaceSchoolIds(Array.from(new Set([...schoolIds, ...visibleIds])));
  };

  const onClearVisible = () => {
    const visible = new Set(filteredSchools.map(schoolRowId));
    replaceSchoolIds(schoolIds.filter((id) => !visible.has(id)));
  };

  const onInvite = async (event) => {
    event.preventDefault();
    setLocalError("");
    if (!email.trim()) {
      setLocalError(t("customerPortal.emailRequired"));
      return;
    }
    if (schoolIds.length === 0) {
      setLocalError(t("customerPortal.atLeastOneSchool"));
      return;
    }
    try {
      await inviteContact({
        name: name.trim(),
        email: email.trim(),
        schools: schoolIds,
        language: localStorage.getItem("i18nextLng") || "tr",
      });
      setName("");
      setEmail("");
      setSchoolQuery("");
      replaceSchoolIds([]);
      await load();
      if (window?.toast?.success) window.toast.success(t("customerPortal.inviteSent"));
    } catch (err) {
      setLocalError(err.message || t("customerPortal.saveFailed"));
    }
  };

  const onResend = async (client) => {
    const id = client.id || client._id;
    setBusyId(id);
    setLocalError("");
    try {
      const schoolsForInvite = (client.schools || []).map((item) => item?.id || item?._id || item).filter(Boolean);
      await inviteContact({
        name: client.name || "",
        email: client.email,
        schools: schoolsForInvite,
        language: localStorage.getItem("i18nextLng") || "tr",
      });
      await load();
      if (window?.toast?.success) window.toast.success(t("customerPortal.inviteSent"));
    } catch (err) {
      setLocalError(err.message || t("customerPortal.saveFailed"));
    } finally {
      setBusyId("");
    }
  };

  const onRemove = async (id) => {
    setBusyId(id);
    try {
      await removeContact(id);
      await load();
    } catch (err) {
      setLocalError(err.message || t("customerPortal.saveFailed"));
    } finally {
      setBusyId("");
    }
  };

  return (
    <React.Fragment>
    <div
      className="centered-form-padding cp-invite"
      style={{
        maxWidth: "1400px",
        width: "100%",
        paddingTop: 10,
        paddingBottom: 40,
        display: "block",
        boxSizing: "border-box",
      }}
    >
      <h2 style={{ marginTop: 0, marginBottom: 8 }}>{t("customerPortal.inviteTitle")}</h2>
      <p className="cp-muted" style={{ marginBottom: 20 }}>
        {t("customerPortal.inviteHint")}
      </p>

      {(localError || error) && <div className="cp-error">{localError || error}</div>}

      <form onSubmit={onInvite}>
        <Grid container spacing={2} className="cp-invite-fields">
          <Grid item xs={12} md={6}>
            <label className="cp-label" htmlFor="cp-inv-name">
              {t("customerPortal.contactName")}
            </label>
            <input
              id="cp-inv-name"
              className="cp-invite-input"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <label className="cp-label" htmlFor="cp-inv-email">
              {t("customerPortal.contactEmail")}
            </label>
            <input
              id="cp-inv-email"
              className="cp-invite-input"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </Grid>
        </Grid>

        <div className="cp-invite-schools">
          <div className="cp-invite-toolbar">
            <div>
              <div className="cp-label" style={{ marginBottom: 0 }}>
                {t("customerPortal.schools")}
              </div>
              <span className="cp-muted">
                {t("customerPortal.selectedSchoolCount", { count: schoolIds.length })}
              </span>
            </div>
            <div className="cp-invite-toolbar-actions">
              <button type="button" className="cp-invite-link" onClick={onSelectVisible} disabled={filteredSchools.length === 0}>
                {t("customerPortal.selectAllSchools")}
              </button>
              <button type="button" className="cp-invite-link" onClick={onClearVisible} disabled={schoolIds.length === 0}>
                {t("customerPortal.clearSchools")}
              </button>
            </div>
          </div>

          {schools.length > 8 && (
            <input
              className="cp-invite-input"
              type="search"
              value={schoolQuery}
              onChange={(event) => setSchoolQuery(event.target.value)}
              placeholder={t("customerPortal.schoolSearchPlaceholder")}
              aria-label={t("customerPortal.schoolSearchPlaceholder")}
            />
          )}

          {schools.length === 0 ? (
            <p className="cp-muted">{t("customerPortal.noSchools")}</p>
          ) : filteredSchools.length === 0 ? (
            <p className="cp-muted">{t("customerPortal.noMatchingSchools")}</p>
          ) : (
            <div className="cp-invite-schools-panel">
              {filteredSchools.map((school) => {
                const id = schoolRowId(school);
                return (
                  <div key={`${schoolBoxKey}_${id}`} className="cp-invite-school-item">
                    <CheckBox
                      id={`invite_school_${id}`}
                      label={school.name}
                      initialValue={schoolIds.includes(id)}
                      initialValid={true}
                      onInput={onSchoolToggle}
                      validators={[VALIDATOR_NONE()]}
                      labelStyle={{ marginTop: 0 }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="cp-actions" style={{ marginTop: 8 }}>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? t("customerPortal.sending") : t("customerPortal.sendInvite")}
          </Button>
        </div>
      </form>

      <h3 style={{ marginTop: 32, marginBottom: 8 }}>{t("customerPortal.contacts")}</h3>
      {contacts.length === 0 && <p className="cp-muted">{t("customerPortal.noContacts")}</p>}
      {contacts.map((client) => {
        const id = client.id || client._id;
        const schoolLabels = (client.schools || [])
          .map((item) => {
            const schoolId = String(item?.id || item?._id || item || "");
            const match = schools.find((school) => schoolRowId(school) === schoolId);
            return match?.name || "";
          })
          .filter(Boolean)
          .join(", ");
        return (
          <div key={id} className="cp-contact-row">
            <div>
              <strong>{client.name}</strong>
              <div className="cp-muted">
                {client.email}
                {client.status === CLIENT_STATUSES.PENDING ? ` · ${t("customerPortal.pending")}` : ""}
              </div>
              {schoolLabels ? <div className="cp-muted">{schoolLabels}</div> : null}
            </div>
            <div className="cp-actions" style={{ marginTop: 0 }}>
              {client.status === CLIENT_STATUSES.PENDING && (
                <Button type="button" inverse disabled={busyId === id} onClick={() => onResend(client)}>
                  {t("customerPortal.resendInvite")}
                </Button>
              )}
              <Button type="button" danger disabled={busyId === id} onClick={() => setRemoveId(id)}>
                {t("customerPortal.removeContact")}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
    <AreYouSureModal
      showModal={Boolean(removeId)}
      text={t("customerPortal.removeContactConfirm")}
      onClose={() => setRemoveId("")}
      onConfirmed={() => {
        if (removeId) onRemove(removeId);
      }}
    />
    </React.Fragment>
  );
};

export default InviteCustomerContacts;
